import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  SmallDashOutlined,
  CalendarOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Input,
  Menu,
  Space,
  Table,
  Tooltip,
  message,
} from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Stack from "@mui/material/Stack";
import { Chip, Badge } from "@mui/material";
import EditNotificationsOutlinedIcon from "@mui/icons-material/EditNotificationsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format } from "date-fns";

import firebase from "../../../config/firebase";
import dayjs from "dayjs";
import DatePickerMenu from "../../../components/dataPicker/dataPickerMenu";
import DialogFileDriver from "../../../components/dialog-fila-driver/dialog-fila-driver";
import DialogFilaTransport, {
  parseTransportQueueResponse,
} from "../../../components/dialog-fila-transport/dialog-fila-transport";
import {
  acceptTransportFromQueue,
  getAcceptTransportErrorMessage,
} from "../../../components/notifications/acceptTransportFromQueue";
import ModalFilter from "../../../components/ModalFilter/ModalFilter";
import YesOrNot from "./yesOrNot";
import freightApi2 from "../../../service/modules/freight.api";
import {
  FREIGHT_STATUS_DESCRIBE_OPTIONS,
  STOPPING_POINT_ACTIVE_STATUS_CODES,
  translatorForTheStatusOfStoppingPoints,
  normalizeFreightStatusDescribe,
} from "../../frete-copy/action/action";

const menuItemStyle = {
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 14px",
  color: "#012442",
  fontWeight: 500,
};

const TTL_STOPPING_POINTS_MS = 10 * 60 * 1000;
const TTL_QUEUE_COUNT_MS = 60 * 1000;

const ENRICH_CONCURRENCY = 8;
const ENRICH_BATCH_MERGE = 20;
const REALTIME_REFRESH_DEBOUNCE_MS = 1500;
/** Limite de páginas extras no front ao varrer filtro (segurança anti-loop). */
const MAX_FILTER_FETCH_ALL_PAGES = 80;
/** Limite do operador `in` no Firestore (Web SDK v9). */
const FIRESTORE_IN_CHUNK_SIZE = 30;

function chunkArray(arr, size) {
  if (!Array.isArray(arr) || size <= 0) return [];
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

function cacheGet(map, key, now = Date.now()) {
  const e = map.get(key);
  if (!e) return undefined;
  if (e.expiresAt <= now) {
    map.delete(key);
    return undefined;
  }
  return e.value;
}

function cacheSet(map, key, value, ttlMs, now = Date.now()) {
  map.set(key, { value, expiresAt: now + ttlMs });
}

function pruneExpiredEntries(map, now = Date.now()) {
  for (const [key, entry] of map.entries()) {
    if (!entry || entry.expiresAt <= now) map.delete(key);
  }
}

function deleteTransportQueueCacheForFreight(map, freightId) {
  if (!freightId) return;
  const prefix = `${freightId}:`;
  for (const key of map.keys()) {
    if (key === freightId || key.startsWith(prefix)) {
      map.delete(key);
    }
  }
}

function resolveEmbarcadorUid(record, user) {
  const fromRecord =
    record?.clientPayment?.uid ||
    record?.shipper?.uid ||
    record?.idShipper ||
    "";
  return String(fromRecord || user?.uidShipper || "").trim();
}

async function mapPool(items, limit, iterator) {
  if (!items.length) return [];
  const results = new Array(items.length);
  let nextIndex = 0;
  async function worker() {
    while (true) {
      const i = nextIndex++;
      if (i >= items.length) break;
      results[i] = await iterator(items[i], i);
    }
  }
  const n = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: n }, () => worker()));
  return results;
}

function fingerprintFreightParams(params) {
  return [
    params.uidShipper ?? "",
    params.idUser ?? "",
    params.affiliates ?? "",
    String(params.viewAll ?? ""),
    String(params.isTransportadora ?? ""),
    params.filterKey ?? "",
  ].join("|");
}

function shipperIsTransport(shipper) {
  if (!shipper || typeof shipper !== "object") return false;
  const value = shipper.isTransport;
  return (
    value === true ||
    value === 1 ||
    String(value).toLowerCase() === "true"
  );
}

function isUserTransportadora(user, shipper) {
  return (
    shipperIsTransport(shipper) ||
    user?.Transportadora === true ||
    user?.transportadora === true
  );
}

/**
 * Escopo do GET /freight/v2.
 * Transportadora (shipper.isTransport): fretes em que transport.idTransport === uidShipper.
 */
function applyFreightV2ScopeParams(params, user, shipper) {
  const carrierUid = String(user?.uidShipper ?? "").trim();
  const isTransportadora = isUserTransportadora(user, shipper);

  if (isTransportadora && carrierUid) {
    params.uidShipper = carrierUid;
    params.isTransportadora = true;
    return;
  }

  const isViewAll = user?.viewALL === true;
  if (!isViewAll) {
    if (user?.uidShipper) {
      params.uidShipper = user.uidShipper;
      if (user?.uid) params.idUser = user.uid;
    }
    if (
      Array.isArray(user?.uidShipperAffiliates) &&
      user.uidShipperAffiliates.length
    ) {
      params.affiliates = user.uidShipperAffiliates.join(",");
    }
  }
  if (typeof user?.viewALL === "boolean") {
    params.viewAll = String(user.viewALL);
  }
}

/** Describe do frete em trânsito (código 05) — alinhado a `STATUS_DESCRIBE_BY_CODE` */
const FREIGHT_IN_TRANSIT_DESCRIBE = "Em transito";
const FREIGHT_IN_TRANSIT_MACRO_CODE = "EM_TRANSITO_FRETE";
const STOPPING_IN_TRANSIT_CODE = "IN_TRANSIT";

/** Remove acentos para busca/comparação (NFD + retirar marcas combinantes). */
function foldDiacritics(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase();
}

/** Parada `IN_TRANSIT` não entra na lista: unificada com o describe do frete em trânsito */
const STOPPING_POINT_CODES_OMITTED_FROM_FILTER = new Set([
  STOPPING_IN_TRANSIT_CODE,
]);

/** Frete (describe 00–08) + paradas (enum) */
const STATUS_FILTER_OPTION_VALUES = [
  ...FREIGHT_STATUS_DESCRIBE_OPTIONS,
  ...STOPPING_POINT_ACTIVE_STATUS_CODES.filter(
    (c) => !STOPPING_POINT_CODES_OMITTED_FROM_FILTER.has(c),
  ),
];

/** Rótulo no filtro (PT); paradas: código do enum; frete: texto describe */
function stoppingPointStatusFilterLabel(code) {
  const t = translatorForTheStatusOfStoppingPoints(code);
  const base =
    typeof t === "string" && t.trim() ? t.trim() : String(code ?? "");
  if (code === "LOADING_PENDING") return `${base} (coleta)`;
  if (code === "UNLOADING_PENDING") return `${base} (entrega)`;
  return base;
}

/** Ordenação local quando existe `activeStatus` (mesma ordem do enum operacional) */
const ACTIVE_STATUS_SORT_RANK = {
  LOADING_PENDING: 1,
  HEADING_TO_PICKUP: 2,
  WAITING_LOADING: 3,
  WAITING_DOCUMENTATION: 4,
  LOADING_COMPLETED: 5,
  [STOPPING_IN_TRANSIT_CODE]: 6,
  UNLOADING_PENDING: 7,
  WAITING_UNLOADING: 8,
  UNLOADING_COMPLETED: 9,
};

function statusFilterOptionLabel(value) {
  if (STOPPING_POINT_ACTIVE_STATUS_CODES.includes(value)) {
    return stoppingPointStatusFilterLabel(value);
  }
  if (value === "Pendente de validação") return "P. Validação";
  if (value === "Pendente de contratação") return "P.Contratação";
  if (value === FREIGHT_IN_TRANSIT_DESCRIBE) return "Em trânsito";
  return value;
}

function filterStatusFilterOptions(searchText) {
  const q = String(searchText ?? "").trim().toLowerCase();
  const qFold = foldDiacritics(q);
  if (!q) return [...STATUS_FILTER_OPTION_VALUES];
  return STATUS_FILTER_OPTION_VALUES.filter((value) => {
    const label = String(statusFilterOptionLabel(value)).toLowerCase();
    const raw = String(value).toLowerCase();
    return (
      label.includes(q) ||
      raw.includes(q) ||
      foldDiacritics(label).includes(qFold) ||
      foldDiacritics(raw).includes(qFold)
    );
  });
}

function serializeFilterKey(dateRange, columnFilters) {
  try {
    return JSON.stringify({
      dr: Array.isArray(dateRange)
        ? dateRange.map((d) =>
            d == null ? null : dayjs(d).format("YYYY-MM-DD"),
          )
        : [],
      cf: columnFilters,
    });
  } catch {
    return "";
  }
}

function normalizeColumnFiltersForV2(columnFilters) {
  if (!columnFilters || typeof columnFilters !== "object") return {};
  const out = { ...columnFilters };
  const st = out["status.describe"];
  if (Array.isArray(st)) {
    const mapped = st
      .map((value) => {
        const s = String(value ?? "").trim();
        if (s === STOPPING_IN_TRANSIT_CODE) return FREIGHT_IN_TRANSIT_DESCRIBE;
        return normalizeFreightStatusDescribe(s) || s;
      })
      .filter(Boolean);
    out["status.describe"] = [...new Set(mapped)];
  } else if (st != null && st !== "") {
    const s = String(st).trim();
    const norm =
      s === STOPPING_IN_TRANSIT_CODE
        ? FREIGHT_IN_TRANSIT_DESCRIBE
        : normalizeFreightStatusDescribe(s) || s;
    out["status.describe"] = [norm];
  }
  return out;
}

/**
 * Parâmetros de filtro do GET /freight/v2 (query string).
 * Valores monetários: número exato como string (paridade com matchesV2Filters no back).
 */
function buildV2FilterQueryParams(dateRange, columnFilters) {
  const cf = normalizeColumnFiltersForV2(columnFilters);
  const q = {};

  if (dateRange && dateRange.length) {
    const [a, b] = dateRange;
    if (a) q.dateColetaDe = dayjs(a).format("YYYY-MM-DD");
    if (b) q.dateColetaAte = dayjs(b).format("YYYY-MM-DD");
  }

  if (cf["numberSerial"])
    q.numberSerial = String(cf["numberSerial"]).trim();
  if (cf["clientPayment.name"])
    q.clientPaymentName = String(cf["clientPayment.name"]).trim();
  if (cf["freight.getDriverFreight.name"])
    q.driverName = String(cf["freight.getDriverFreight.name"]).trim();
  if (cf["firstDelivery.city"])
    q.origem = String(cf["firstDelivery.city"]).trim();
  if (cf["lastDelivery.city"])
    q.destino = String(cf["lastDelivery.city"]).trim();

  appendMoneyFilterToV2Query(q, cf["freight.valueFreightage"], "freightValue");
  appendMoneyFilterToV2Query(q, cf["driver.valueNegotiated"], "payingDriver");

  const st = cf["status.describe"];
  if (Array.isArray(st) && st.length) {
    const freightSet = new Set(FREIGHT_STATUS_DESCRIBE_OPTIONS);
    const values = st
      .map((c) => String(c ?? "").trim())
      .filter(
        (c) =>
          STOPPING_POINT_ACTIVE_STATUS_CODES.includes(c) || freightSet.has(c),
      );
    const normalized = [...new Set(values)].map((v) =>
      v === FREIGHT_IN_TRANSIT_DESCRIBE ? FREIGHT_IN_TRANSIT_MACRO_CODE : v,
    );
    if (normalized.length === 1) {
      // Backend v2 works better with scalar status value.
      q.status = normalized[0];
    } else if (normalized.length > 1) {
      q.status = normalized;
    }
  }

  return q;
}

function omitRecentlyDeletedRows(rows, deletedIds) {
  if (!Array.isArray(rows) || !deletedIds?.size) return rows;
  return rows.filter((row) => row?.id && !deletedIds.has(row.id));
}

/** Mapeia o retorno do backend para o shape da tabela (payload “flat” ou já aninhado). */
function mapFreightFromApi(item) {
  const id = item.id || item._id;

  const clientPayment = item.clientPayment || {
    uid: item.client?.uid,
    name: item.client?.name,
    cnpj: item.client?.cnpj,
  };

  const driverNameFromApi =
    item.driver?.driverName ??
    item.driver?.name ??
    item.freight?.getDriverFreight?.name;

  const freight = item.freight
    ? {
        ...item.freight,
        getDriverFreight: {
          ...item.freight.getDriverFreight,
          ...(driverNameFromApi != null && String(driverNameFromApi).trim() !== ""
            ? { name: driverNameFromApi }
            : {}),
        },
      }
    : {
        valueFreightage: item.valueFreightage ?? item.freightValue ?? 0,
        getDriverFreight: {
          uidDriver: item.driver?.uid,
          name: driverNameFromApi,
        },
      };

  const driver = item.driver || {
    valueNegotiated:
      item.paymentToDriver ??
      item.driverValueNegotiated ??
      item.driver?.valueNegotiated ??
      0,
    valueInitial: item.driver?.valueInitial ?? 0,
    valueFinal: item.driver?.valueFinal ?? 0,
    paymentCondition: item.driver?.paymentCondition,
  };

  const firstDelivery = item.firstDelivery || {
    city: item.origin?.city,
    address: item.origin?.address,
    cep: item.origin?.cep,
    dateColeta: item.origin?.dateColeta || item.dateColeta,
    coordinates: item.origin?.coordinates,
    name: item.origin?.name,
    uf: item.origin?.uf,
  };

  const lastDelivery = item.lastDelivery || {
    city: item.destination?.city,
    address: item.destination?.address,
    cep: item.destination?.cep,
    coordinates: item.destination?.coordinates,
    name: item.destination?.name,
  };

  const baseStatus = item.status || {};
  const rawDescribe =
    baseStatus.describe ??
    item.statusDescribe ??
    item.status_text ??
    "";
  const describeNorm =
    normalizeFreightStatusDescribe(String(rawDescribe).trim()) ||
    String(rawDescribe).trim();
  const status = { ...baseStatus, describe: describeNorm };

  const activeStatus = item.activeStatus || null;

  return {
    id,
    numberSerial: item.numberSerial ?? item.code ?? item.serial,
    clientPayment,
    freight,
    driver,
    firstDelivery,
    lastDelivery,
    status,
    activeStatus,
  };
}

function moneyToInt(value) {
  if (value == null) return 0;
  if (typeof value === "number") return Math.round(value);
  const str = String(value);
  return Math.round(Number(str.replace(/\./g, "").replace(",", ".")));
}

function appendMoneyFilterToV2Query(q, fieldValue, queryKey) {
  if (fieldValue == null || fieldValue === "") return;
  if (typeof fieldValue === "object" && fieldValue.operator) {
    if (fieldValue.operator === "equals" && fieldValue.value !== undefined)
      q[queryKey] = String(Number(fieldValue.value));
  } else {
    const n = typeof fieldValue === "number" ? fieldValue : moneyToInt(fieldValue);
    q[queryKey] = String(Number(n));
  }
}

/** Há filtros enviados ao GET /freight/v2 (coluna ou data). */
function hasActiveV2Filters(dateRange, columnFilters) {
  const q = buildV2FilterQueryParams(dateRange, columnFilters);
  return Object.keys(q).length > 0;
}

function mergeFreightRowsById(rows) {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (!row?.id) return;
    const prev = byId.get(row.id);
    byId.set(row.id, prev ? { ...prev, ...row } : row);
  });
  return Array.from(byId.values());
}

function parseFreightV2ListResponse(result, limit, cursorForPage) {
  const payload = result?.data;
  const items = Array.isArray(payload?.data)
    ? payload.data
    : payload?.data?.items || [];
  const pagination = payload?.pagination ?? {};
  const nextCursor = pagination?.nextCursor ?? null;
  const serverHasMore = pagination?.hasMore === true;
  const hasFullPage = items.length >= limit;
  const hasValidNextCursor =
    typeof nextCursor === "string" &&
    nextCursor.trim().length > 0 &&
    nextCursor !== cursorForPage;
  const hasMore = serverHasMore && hasFullPage && hasValidNextCursor;
  const hasMoreForFetchAll = serverHasMore && hasValidNextCursor;
  return {
    items,
    activeStatus: payload?.activeStatus ?? null,
    nextCursor,
    hasMore,
    hasMoreForFetchAll,
  };
}

function renderStatusColor(status) {
  const chipStyle = { minWidth: "100px", maxWidth: "150px" };
  const commonChip = (label, color) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Chip
        label={label}
        sx={{
          ...chipStyle,
          backgroundColor: color,
          color: "#F9FAFB",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          height: 28,
          px: 0.5,
          boxShadow:
            "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
          border: "1px solid rgba(1,36,66,0.08)",
          "& .MuiChip-label": {
            px: 1,
          },
        }}
      />
    </Stack>
  );

  switch (status) {
    
    case "Contratado":
      return commonChip(status, "#059669");
    case "P. Validação":
    case "Pendente de validação":
      return commonChip("P. Validação", "#A855F7");
    case "P.Contratação":
    case "Pendente de contratação":
      return commonChip("P.Contratação", "#F97316");
    case FREIGHT_IN_TRANSIT_DESCRIBE:
    case "Em trânsito":
      return commonChip("Em trânsito", "#16A34A");
    case "Entregue":
      return commonChip(status, "#2563EB");
    case "Finalizado":
      return commonChip(status, "#6B7280");
    case "Cancelado":
      return commonChip(status, "#B91C1C");
    case "Deslocando-se para a coleta":
      return commonChip(status, "#22C55E");
    case "Aguardando Carregamento":
      return commonChip(status, "#F59E0B");
    case "Aguardando Documentação":
      return commonChip(status, "#FBBF24");
    case "Aguardando Descarregamento":
      return commonChip(status, "#EAB308");
    default:
      return commonChip(status || "—", "#020202");
  }
}

// ========================
// Badge com dados em memória / sob demanda
// ========================
const DriverBadge = ({
  recordId,
  statusDescribe,
  queueCount = 0,
  queueLoading = false,
}) => {
  if (
    statusDescribe === "Finalizado" ||
    statusDescribe === "Contratado" ||
    statusDescribe === "Cancelado"
  ) {
    return <EditNotificationsOutlinedIcon />;
  }

  return (
    <Link to={"#" + recordId}>
      {queueLoading ? (
        <EditNotificationsOutlinedIcon color="primary" />
      ) : queueCount > 0 ? (
        <Badge badgeContent={queueCount} color="success">
          <EditNotificationsOutlinedIcon color="primary" />
        </Badge>
      ) : (
        <EditNotificationsOutlinedIcon color="primary" />
      )}
    </Link>
  );
};

const TransportBadge = ({
  recordId,
  queueCount = 0,
  queueLoading = false,
}) => (
  <Link to={"#" + recordId}>
    {queueLoading ? (
      <LocalShippingOutlinedIcon color="primary" />
    ) : queueCount > 0 ? (
      <Badge badgeContent={queueCount} color="success">
        <LocalShippingOutlinedIcon color="primary" />
      </Badge>
    ) : (
      <LocalShippingOutlinedIcon color="primary" />
    )}
  </Link>
);


function reorderArray(arr, fromIdx, toIdx) {
  if (!Array.isArray(arr)) return [];
  if (
    fromIdx < 0 ||
    toIdx < 0 ||
    fromIdx >= arr.length ||
    toIdx >= arr.length ||
    fromIdx === toIdx
  ) {
    return arr.slice();
  }
  const next = arr.slice();
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
}

function applyColumnOrder(baseColumns, order) {
  if (!Array.isArray(baseColumns) || !baseColumns.length) return [];
  if (!Array.isArray(order) || !order.length) return baseColumns.slice();
  const byKey = new Map(baseColumns.map((c) => [c.key, c]));
  const seen = new Set();
  const ordered = [];
  for (const key of order) {
    const col = byKey.get(key);
    if (col && !seen.has(key)) {
      ordered.push(col);
      seen.add(key);
    }
  }
  for (const col of baseColumns) {
    if (!seen.has(col.key)) {
      ordered.push(col);
      seen.add(col.key);
    }
  }
  return ordered;
}

const DraggableHeaderCell = ({
  columnKey,
  isDragging,
  isDragOver,
  onReorder,
  onDragStateChange,
  style,
  children,
  ...restProps
}) => {
  if (!columnKey || typeof onReorder !== "function") {
    return (
      <th {...restProps} style={style}>
        {children}
      </th>
    );
  }

  const handleDragStart = (e) => {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try {
        e.dataTransfer.setData("text/plain", String(columnKey));
      } catch {
        // noop
      }
    }
    onDragStateChange?.({ type: "start", columnKey });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    onDragStateChange?.({ type: "over", columnKey });
  };

  const handleDragLeave = () => {
    onDragStateChange?.({ type: "leave", columnKey });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    let fromKey = null;
    try {
      fromKey = e.dataTransfer?.getData("text/plain") || null;
    } catch {
      // noop
    }
    if (fromKey) onReorder(fromKey, columnKey);
    onDragStateChange?.({ type: "end" });
  };

  const handleDragEnd = () => {
    onDragStateChange?.({ type: "end" });
  };

  const dragStyle = {
    ...style,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    opacity: isDragging ? 0.5 : style?.opacity ?? 1,
    boxShadow: isDragOver ? "inset 4px 0 0 0 #1677ff" : style?.boxShadow,
    transition: "box-shadow 120ms ease, opacity 120ms ease",
  };

  const handleIconColor = isDragging || isDragOver ? "#1677ff" : "#012442";

  return (
    <th
      {...restProps}
      style={dragStyle}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Tooltip title="Arraste para reordenar a coluna">
          <HolderOutlined
            aria-hidden="true"
            style={{
              color: handleIconColor,
              fontSize: 14,
              flexShrink: 0,
              cursor: isDragging ? "grabbing" : "grab",
              transition: "color 120ms ease",
            }}
          />
        </Tooltip>
        {children}
      </span>
    </th>
  );
};

// ========================
// Componente principal
// ========================
const DataFreteTableCopy = ({
  cleanFilter,
  openFilter,
  onCleanFilterHandled,
  onOpenFilterHandled,
}) => {
  const user = useSelector((state) => state.user);
  const shipper = useSelector((state) => state.shipper);
  const [resolvedShipper, setResolvedShipper] = useState(null);
  const [shipperScopeLoading, setShipperScopeLoading] = useState(false);

  const effectiveShipper = resolvedShipper ?? shipper ?? null;
  const isTransportadoraUser = useMemo(
    () => isUserTransportadora(user, effectiveShipper),
    [user, effectiveShipper],
  );

  const canViewFreightDriver = user?.permissoes?.freightDriver?.isView;
  const canViewFreightFreight = user?.permissoes?.freightFreight?.isView;
  
  const db = firebase.firestore();

  useEffect(() => {
    if (shipperIsTransport(shipper)) {
      setResolvedShipper(shipper);
      setShipperScopeLoading(false);
      return undefined;
    }
    const carrierUid = String(user?.uidShipper ?? "").trim();
    if (!carrierUid) {
      setResolvedShipper(null);
      setShipperScopeLoading(false);
      return undefined;
    }

    let cancelled = false;
    setShipperScopeLoading(true);
    db.collection("shipper")
      .doc(carrierUid)
      .get()
      .then((doc) => {
        if (!cancelled) {
          setResolvedShipper(doc.exists ? doc.data() : null);
        }
      })
      .catch((error) => {
        console.warn("[FreteList] Não foi possível carregar shipper:", error);
        if (!cancelled) {
          setResolvedShipper(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setShipperScopeLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [db, user?.uidShipper, shipper]);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openTransportModal, setOpenTransportModal] = useState(false);
  const [openModalFilter, setOpenModalFilter] = useState(false);
  const [driversInFila, setDriversInFila] = useState([]);
  const [transportsInFila, setTransportsInFila] = useState([]);
  const [transportFreightId, setTransportFreightId] = useState(null);
  const [transportFreightShipperUid, setTransportFreightShipperUid] =
    useState(null);
  const [acceptingTransportId, setAcceptingTransportId] = useState(null);
  const [datas, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isFilterApplying, setIsFilterApplying] = useState(false);
  const [filterFetchingAll, setFilterFetchingAll] = useState(false);

  const [dateRange, setDateRange] = useState([]);
  const [draftDateRange, setDraftDateRange] = useState([]);
  const hasLoadedRef = useRef(false);

  const [columnFilters, setColumnFilters] = useState({});
  const [statusSearchText, setStatusSearchText] = useState("");
  const [sorterConfig, setSorterConfig] = useState({
    columnKey: null,
    order: null,
  });

  const [columnOrder, setColumnOrder] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const draggingKeyRef = useRef(null);
  const orderedColumnsKeysRef = useRef([]);

  const filterKey = useMemo(
    () => serializeFilterKey(dateRange, columnFilters),
    [dateRange, columnFilters],
  );

  const serverFiltersActive = useMemo(
    () => hasActiveV2Filters(dateRange, columnFilters),
    [dateRange, columnFilters, filterKey],
  );

  const getStatusDescribeForRecord = useCallback((record) => {
    if (record?.activeStatus != null) {
      const translated = translatorForTheStatusOfStoppingPoints(record.activeStatus);
      if (typeof translated === "string" && translated.trim()) return translated;
    }
    return record?.status?.describe ?? "";
  }, []);

  // Estados de paginação (cursor no servidor — GET /freight/v2)
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [listHasMore, setListHasMore] = useState(false);
  /** Map: número da página (>=2) → cursor a enviar na query para obter essa página */
  const cursorRequestByPageRef = useRef(new Map());
  const trimCursorMapFromPage = useCallback((startPage) => {
    if (!Number.isFinite(startPage)) return;
    const nextMap = new Map();
    cursorRequestByPageRef.current.forEach((value, key) => {
      if (key < startPage) nextMap.set(key, value);
    });
    cursorRequestByPageRef.current = nextMap;
  }, []);

  const searchInput = useRef(null);
  const statusFilterRef = useRef(null);
  const lastFetchSigRef = useRef("");
  const inFlightFetchSigRef = useRef("");
  const filterKeyForFetchRef = useRef("");
  const pageSizeForFetchRef = useRef(50);
  const datasRef = useRef([]);
  const fetchDataRef = useRef(null);
  const realtimeRefreshTimerRef = useRef(null);

  const listVersionRef = useRef(0);

  const recentlyDeletedIdsRef = useRef(new Set());
  useEffect(() => {
    datasRef.current = datas;
  }, [datas]);

  useEffect(() => {
    filterFetchingAllRef.current = filterFetchingAll;
  }, [filterFetchingAll]);

  useEffect(() => {
    isFilterApplyingRef.current = isFilterApplying;
  }, [isFilterApplying]);

  const storageKey = (uid, key) => `${uid}:${key}`;
  const stoppingPointsCacheRef = useRef(new Map());
  const queueCountCacheRef = useRef(new Map());
  const transportQueueCountCacheRef = useRef(new Map());
  const enrichRunIdRef = useRef(0);
  const enrichAbortRef = useRef({ cancelled: false });
  const filterFetchRunIdRef = useRef(0);
  const filterFetchAbortRef = useRef({ cancelled: false });
  const filterFetchingAllRef = useRef(false);
  const isFilterApplyingRef = useRef(false);
  const activeStatusRef = useRef(null);
  const [queueUiByFreightId, setQueueUiByFreightId] = useState({});
  const [transportQueueUiByFreightId, setTransportQueueUiByFreightId] = useState({});

  const clearSessionCaches = useCallback(() => {
    stoppingPointsCacheRef.current.clear();
    queueCountCacheRef.current.clear();
    transportQueueCountCacheRef.current.clear();
    setQueueUiByFreightId({});
    setTransportQueueUiByFreightId({});
  }, []);

  const fetchQueueCountCached = useCallback(async (freightId) => {
    const hit = cacheGet(queueCountCacheRef.current, freightId);
    if (hit !== undefined) return hit;
    try {
      const res = await freightApi2.queue(freightId);
      const queue = Array.isArray(res.data) ? res.data : res.data?.items || [];
      const count = queue.length;
      cacheSet(queueCountCacheRef.current, freightId, count, TTL_QUEUE_COUNT_MS);
      return count;
    } catch {
      return 0;
    }
  }, []);

  const prefetchQueueCountForFreight = useCallback(
    async (freightId) => {
      if (!freightId) return;
      setQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { ...(prev[freightId] || {}), loading: true },
      }));
      const count = await fetchQueueCountCached(freightId);
      setQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { count, loading: false },
      }));
    },
    [fetchQueueCountCached],
  );

  const fetchTransportQueueCountCached = useCallback(async (freightId, shipperUid) => {
    const isShipper = shipperUid || user?.uidShipper || "";
    if (!freightId || !isShipper) return 0;

    const cacheKey = `${freightId}:${isShipper}`;
    const hit = cacheGet(transportQueueCountCacheRef.current, cacheKey);
    if (hit !== undefined) return hit;
    try {
      const res = await freightApi2.transportQueue(freightId, isShipper);
      const queue = parseTransportQueueResponse(res);
      const count = queue.length;
      cacheSet(
        transportQueueCountCacheRef.current,
        cacheKey,
        count,
        TTL_QUEUE_COUNT_MS,
      );
      return count;
    } catch {
      return 0;
    }
  }, [user?.uidShipper]);

  const prefetchTransportQueueCountForFreight = useCallback(
    async (freightId, shipperUid) => {
      if (!freightId) return;
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { ...(prev[freightId] || {}), loading: true },
      }));
      const count = await fetchTransportQueueCountCached(freightId, shipperUid);
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { count, loading: false },
      }));
    },
    [fetchTransportQueueCountCached],
  );

  const handleDateRangeChange = (dates) => {
    setDraftDateRange(dates ? [dates[0], dates[1]] : []);
  };

  const enrichFreightRows = useCallback(
    async (rows, runId) => {
      if (!Array.isArray(rows) || !rows.length) {
        return { total: 0, stoppingPointsErr: 0 };
      }

      const sanity = { total: rows.length, stoppingPointsErr: 0 };
      let mergeBatch = [];
      const flushMerge = (batch) => {
        if (!batch.length) return;
        if (enrichAbortRef.current.cancelled || runId !== enrichRunIdRef.current) {
          return;
        }
        setData((prev) => {
          const base = omitRecentlyDeletedRows(prev, recentlyDeletedIdsRef.current);
          const byId = new Map(base.map((r) => [r.id, { ...r }]));
          batch.forEach(({ id, patch }) => {
            if (recentlyDeletedIdsRef.current.has(id)) return;
            const cur = byId.get(id);
            if (cur) byId.set(id, { ...cur, ...patch });
          });
          return base.map((r) => byId.get(r.id) || r);
        });
      };

      await mapPool(rows, ENRICH_CONCURRENCY, async (item) => {
        if (enrichAbortRef.current.cancelled || runId !== enrichRunIdRef.current)
          return;
        if (!item?.id) return;

        let stoppingPoints = cacheGet(stoppingPointsCacheRef.current, item.id);
        if (stoppingPoints === undefined) {
          try {
            const snapshot = await db
              .collection("freight")
              .doc(item.id)
              .collection("stopping_points")
              .get();
            stoppingPoints = snapshot.docs.map((spDoc) => ({
              id: spDoc.id,
              ...spDoc.data(),
            }));
            cacheSet(
              stoppingPointsCacheRef.current,
              item.id,
              stoppingPoints,
              TTL_STOPPING_POINTS_MS,
            );
          } catch {
            stoppingPoints = [];
            sanity.stoppingPointsErr++;
          }
        }

        mergeBatch.push({ id: item.id, patch: { stopping_points: stoppingPoints } });
        if (mergeBatch.length >= ENRICH_BATCH_MERGE) {
          const batch = mergeBatch;
          mergeBatch = [];
          flushMerge(batch);
        }
      });

      flushMerge(mergeBatch);
      return sanity;
    },
    [db],
  );

  // ========================
  // Fetch API — GET /freight/v2 (filtros e paginação no servidor)
  // ========================
  const fetchData = useCallback(async (options = {}) => {
    const forceRefresh = options.forceRefresh === true;
    const isFilterChange = options.isFilterChange === true;
    const effectivePage = options.page ?? current;
    const listVersionAtFetchStart = listVersionRef.current;
    const filtersActive = hasActiveV2Filters(dateRange, columnFilters);
    const fetchAllFilteredResults =
      filtersActive && effectivePage === 1 && !options.skipFetchAllFiltered;
    const limit = fetchAllFilteredResults
      ? 100
      : Math.min(Math.max(1, pageSize), 100);

    if (!user) return;
    const now = Date.now();
    pruneExpiredEntries(stoppingPointsCacheRef.current, now);
    pruneExpiredEntries(queueCountCacheRef.current, now);
    pruneExpiredEntries(transportQueueCountCacheRef.current, now);
    if (forceRefresh) {
      lastFetchSigRef.current = "";
      clearSessionCaches();
    }

    const commitListRows = (rows) => {
      if (listVersionAtFetchStart !== listVersionRef.current) return;
      setData(omitRecentlyDeletedRows(rows, recentlyDeletedIdsRef.current));
    };

    const params = {};
    applyFreightV2ScopeParams(params, user, effectiveShipper);

    Object.assign(params, buildV2FilterQueryParams(dateRange, columnFilters));
    params.limit = limit;

    const cursorForPage =
      effectivePage <= 1
        ? undefined
        : cursorRequestByPageRef.current.get(effectivePage);
    if (effectivePage > 1 && cursorForPage === undefined) {
      message.warning(
        "Use a paginação em sequência (não é possível pular páginas).",
      );
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (cursorForPage) params.cursor = cursorForPage;

    const fingerprint = fingerprintFreightParams({ ...params, filterKey });
    const requestSig = `${fingerprint}|${effectivePage}|${limit}|${params.cursor ?? ""}`;
    if (
      !forceRefresh &&
      inFlightFetchSigRef.current &&
      inFlightFetchSigRef.current === requestSig
    ) {
      return;
    }

    if (!forceRefresh && datasRef.current.length > 0) {
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
      setRefreshing(false);
    }

    enrichAbortRef.current.cancelled = true;
    enrichRunIdRef.current += 1;
    const runId = enrichRunIdRef.current;
    enrichAbortRef.current = { cancelled: false };

    filterFetchAbortRef.current.cancelled = true;
    filterFetchRunIdRef.current += 1;
    const filterFetchRunId = filterFetchRunIdRef.current;
    filterFetchAbortRef.current = { cancelled: false };
    if (!fetchAllFilteredResults) {
      setFilterFetchingAll(false);
    }

    let deferClearFilterApplying = false;

    try {
      if (isFilterChange) setIsFilterApplying(true);
      inFlightFetchSigRef.current = requestSig;
      /**
       * IMPORTANTE:
       * - Não envie uids/viewALL pelo front como "regra de segurança".
       * - O backend deve usar o usuário do token e aplicar a regra.
       *
       * Se você quiser mandar só pra “debug”, mande e o backend IGNORA se não tiver permissão.
       */
      const result = await freightApi2.getAllFreightsV2(params);
      const {
        items,
        activeStatus,
        nextCursor,
        hasMore,
        hasMoreForFetchAll,
      } = parseFreightV2ListResponse(result, limit, cursorForPage);

      activeStatusRef.current = activeStatus;

      if (fetchAllFilteredResults) {
        setListHasMore(hasMoreForFetchAll);
        trimCursorMapFromPage(2);
      } else {
        setListHasMore(hasMore);
        if (hasMore) {
          cursorRequestByPageRef.current.set(effectivePage + 1, nextCursor);
        } else {
          trimCursorMapFromPage(effectivePage + 1);
        }
      }

      const mapped = items.map(mapFreightFromApi);

      if (effectivePage > 1 && mapped.length === 0) {
        trimCursorMapFromPage(effectivePage);
        setCurrent((prev) => Math.max(1, Math.min(prev - 1, effectivePage - 1)));
        setLoading(false);
        setRefreshing(false);
        message.warning("Nao ha dados para esta pagina. Retornando para a pagina anterior.");
        return;
      }

      if (
        runId !== enrichRunIdRef.current ||
        listVersionAtFetchStart !== listVersionRef.current
      ) {
        return;
      }

      const willFetchAllFiltered =
        fetchAllFilteredResults && hasMoreForFetchAll;
      if (willFetchAllFiltered) {
        setFilterFetchingAll(true);
        filterFetchingAllRef.current = true;
      }

      // Fase 1: render base imediata
      commitListRows(mapped);
      setLoading(false);
      setRefreshing(false);
      if (isFilterChange && !willFetchAllFiltered) setIsFilterApplying(false);

      const runEnrichWithWarning = async (rowsToEnrich) => {
        const sanity = await enrichFreightRows(rowsToEnrich, runId);
        if (
          sanity.total > 0 &&
          sanity.stoppingPointsErr / sanity.total > 0.1
        ) {
          message.warning(
            `Muitas falhas ao ler stopping_points no Firestore (${sanity.stoppingPointsErr}/${sanity.total}).`,
          );
        }
      };

      if (willFetchAllFiltered) {
        deferClearFilterApplying = true;

        const loadAllFilteredPages = async () => {
          let accumulated = mergeFreightRowsById(mapped);
          let cursor = nextCursor;
          let more = hasMoreForFetchAll;
          let pageCount = 0;
          const seenCursors = new Set();

          try {
            while (more) {
              if (
                filterFetchAbortRef.current.cancelled ||
                filterFetchRunId !== filterFetchRunIdRef.current ||
                runId !== enrichRunIdRef.current ||
                listVersionAtFetchStart !== listVersionRef.current
              ) {
                return;
              }

              pageCount += 1;
              if (pageCount > MAX_FILTER_FETCH_ALL_PAGES) {
                console.warn(
                  "[frete-list] limite de paginas do filtro atingido; exibindo resultados parciais.",
                );
                break;
              }

              if (!cursor || seenCursors.has(cursor)) {
                break;
              }
              seenCursors.add(cursor);

              const pageParams = { ...params, limit: 100, cursor };
              const pageResult = await freightApi2.getAllFreightsV2(pageParams);
              const parsed = parseFreightV2ListResponse(
                pageResult,
                100,
                cursor,
              );
              if (parsed.activeStatus != null) {
                activeStatusRef.current = parsed.activeStatus;
              }

              const pageMapped = parsed.items.map(mapFreightFromApi);
              const prevLen = accumulated.length;
              accumulated = mergeFreightRowsById([
                ...accumulated,
                ...pageMapped,
              ]);
              const next = parsed.nextCursor;
              if (
                !parsed.hasMoreForFetchAll ||
                !next ||
                next === cursor ||
                (accumulated.length === prevLen && !pageMapped.length)
              ) {
                more = false;
              } else {
                cursor = next;
                more = parsed.hasMoreForFetchAll;
              }

              // Atualiza UI só no fim ou a cada 3 lotes (evita reativar listeners em loop).
              if (!more || pageCount % 3 === 0) {
                commitListRows(accumulated);
                setListHasMore(more);
              }
            }

            if (
              filterFetchAbortRef.current.cancelled ||
              filterFetchRunId !== filterFetchRunIdRef.current ||
              runId !== enrichRunIdRef.current ||
              listVersionAtFetchStart !== listVersionRef.current
            ) {
              return;
            }

            commitListRows(accumulated);
            setListHasMore(false);
            trimCursorMapFromPage(2);

            await runEnrichWithWarning(accumulated);
          } catch (pageErr) {
            if (
              filterFetchRunId !== filterFetchRunIdRef.current ||
              runId !== enrichRunIdRef.current
            ) {
              return;
            }
            console.error("Erro ao carregar paginas filtradas:", pageErr);
            message.warning(
              "Nao foi possivel carregar todos os resultados do filtro. Exibindo os dados ja obtidos.",
            );
            await runEnrichWithWarning(datasRef.current);
          } finally {
            if (filterFetchRunId === filterFetchRunIdRef.current) {
              filterFetchingAllRef.current = false;
              setFilterFetchingAll(false);
              setIsFilterApplying(false);
            }
          }
        };

        void loadAllFilteredPages();
        return;
      }

      await runEnrichWithWarning(mapped);
    } catch (err) {
      if (runId !== enrichRunIdRef.current) return;
      const statusCode = err?.response?.status;
      setRefreshing(false);
      setLoading(false);
      setListHasMore(false);
      trimCursorMapFromPage(current + 1);
      if (datasRef.current.length > 0) {
        message.warning("Não foi possível atualizar; exibindo dados anteriores.");
      } else {
        if (isFilterChange && statusCode === 500) {
          message.error("Erro interno ao aplicar filtro de motorista. Tente novamente.");
        } else {
          message.error("Erro ao buscar fretes na API");
        }
        setData([]);
      }
      if (isFilterChange) setIsFilterApplying(false);
      inFlightFetchSigRef.current = "";
    } finally {
      if (isFilterChange && !deferClearFilterApplying) {
        setIsFilterApplying(false);
      }
      if (inFlightFetchSigRef.current === requestSig) {
        inFlightFetchSigRef.current = "";
      }
    }
  }, [
    clearSessionCaches,
    enrichFreightRows,
    user,
    effectiveShipper,
    isTransportadoraUser,
    current,
    pageSize,
    filterKey,
    dateRange,
    columnFilters,
    trimCursorMapFromPage,
  ]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    clearSessionCaches();
  }, [user?.uid, clearSessionCaches]);

  useEffect(() => {
    recentlyDeletedIdsRef.current.clear();
    listVersionRef.current += 1;
    setData([]);
    setLoading(true);
    setRefreshing(false);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    if (shipperScopeLoading) return;
    const fkChanged = filterKeyForFetchRef.current !== filterKey;
    const szChanged = pageSizeForFetchRef.current !== pageSize;
    filterKeyForFetchRef.current = filterKey;
    pageSizeForFetchRef.current = pageSize;
    if (fkChanged || szChanged) {
      cursorRequestByPageRef.current = new Map();
      setCurrent(1);
    }
    const effectivePage = fkChanged || szChanged ? 1 : current;
    const sig = `${filterKey}|${effectivePage}|${pageSize}`;
    if (lastFetchSigRef.current === sig) return;
    lastFetchSigRef.current = sig;
    fetchData({ page: effectivePage, isFilterChange: fkChanged });
  }, [user?.uid, user?.uidShipper, isTransportadoraUser, shipperScopeLoading, filterKey, current, pageSize, fetchData]);

  /**
   * Chave estável dos IDs visíveis: enquanto o conjunto de IDs da página
   * não muda (ex.: enriquecimento via `flushMerge` apenas mescla patches),
   * o listener não é recriado.
   */
  const visibleIdsKey = useMemo(() => {
    if (!Array.isArray(datas) || !datas.length) return "";
    return datas
      .map((row) => row?.id)
      .filter(Boolean)
      .slice()
      .sort()
      .join("|");
  }, [datas]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    if (!visibleIdsKey) return undefined;

    const visibleIds = visibleIdsKey.split("|").filter(Boolean);
    if (!visibleIds.length) return undefined;

    const visibleIdSet = new Set(visibleIds);
    const chunks = chunkArray(visibleIds, FIRESTORE_IN_CHUNK_SIZE);
    const documentId = "__name__";

    const handleSnapshot = (snapshot) => {
      if (filterFetchingAllRef.current || isFilterApplyingRef.current) {
        return;
      }

      const changedIds = new Set();
      let hasRemovals = false;
      snapshot.docChanges().forEach((change) => {
        const id = change?.doc?.id;
        if (!id || !visibleIdSet.has(id)) return;
        changedIds.add(id);
        if (change.type === "removed") hasRemovals = true;
        stoppingPointsCacheRef.current.delete(id);
        queueCountCacheRef.current.delete(id);
        deleteTransportQueueCacheForFreight(transportQueueCountCacheRef.current, id);
        setQueueUiByFreightId((prev) => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setTransportQueueUiByFreightId((prev) => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      });
      if (!changedIds.size) return;
      if (hasRemovals) {
        changedIds.forEach((id) => recentlyDeletedIdsRef.current.add(id));
        setData((prev) => {
          if (!Array.isArray(prev) || !prev.length) return prev;
          return prev.filter((row) => !changedIds.has(row.id));
        });
      }
      if (filterFetchingAllRef.current || isFilterApplyingRef.current) {
        return;
      }
      if (serverFiltersActive) {
        // Com isso evita o recarregamento dos dados, causando divergencia entre a lista e o filtro.
        return;
      }
      if (realtimeRefreshTimerRef.current) {
        clearTimeout(realtimeRefreshTimerRef.current);
      }
      realtimeRefreshTimerRef.current = setTimeout(() => {
        if (filterFetchingAllRef.current || isFilterApplyingRef.current) {
          return;
        }
        fetchDataRef.current?.({
          page: current,
        });
      }, REALTIME_REFRESH_DEBOUNCE_MS);
    };

    const unsubscribers = chunks.map((chunk) =>
      db
        .collection("freight")
        .where(documentId, "in", chunk)
        .onSnapshot(handleSnapshot, () => {
          // noop: falha do listener não deve quebrar a tela
        }),
    );

    return () => {
      if (realtimeRefreshTimerRef.current) {
        clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
      unsubscribers.forEach((unsub) => {
        try {
          unsub?.();
        } catch {
          // noop
        }
      });
    };
  }, [db, user?.uid, current, visibleIdsKey, serverFiltersActive]);

  // ========================
  // localStorage filtros
  // ========================
  useEffect(() => {
    if (!user?.uid) return;

    try {
      const savedDateRange = localStorage.getItem(
        storageKey(user.uid, "freightDateRangeFilter"),
      );
      const savedColumnFilters = localStorage.getItem(
        storageKey(user.uid, "freightColumnFilters"),
      );
      const savedSorter = localStorage.getItem(
        storageKey(user.uid, "freightTableSorter"),
      );
      const savedColumnOrder = localStorage.getItem(
        storageKey(user.uid, "freightColumnOrder"),
      );

      if (savedColumnOrder) {
        try {
          const parsedOrder = JSON.parse(savedColumnOrder);
          if (
            Array.isArray(parsedOrder) &&
            parsedOrder.every((k) => typeof k === "string")
          ) {
            setColumnOrder(parsedOrder);
          } else {
            setColumnOrder(null);
          }
        } catch {
          setColumnOrder(null);
        }
      } else {
        setColumnOrder(null);
      }

      // Carrega os filtros de coluna
      if (savedColumnFilters) {
        const parsedFilters = JSON.parse(savedColumnFilters);
        if (parsedFilters && typeof parsedFilters === "object") {
          setColumnFilters(normalizeColumnFiltersForV2(parsedFilters));
        } else {
          setColumnFilters({});
        }
      } else {
        setColumnFilters({});
      }

      if (savedSorter) {
        const parsedSorter = JSON.parse(savedSorter);
        if (
          parsedSorter &&
          typeof parsedSorter === "object" &&
          typeof parsedSorter.columnKey === "string" &&
          (parsedSorter.order === "ascend" || parsedSorter.order === "descend")
        ) {
          setSorterConfig({
            columnKey: parsedSorter.columnKey,
            order: parsedSorter.order,
          });
        } else {
          setSorterConfig({ columnKey: null, order: null });
        }
      } else {
        setSorterConfig({ columnKey: null, order: null });
      }

      // Carrega o filtro de data e converte strings para dayjs
      if (savedDateRange) {
        const parsedDateRange = JSON.parse(savedDateRange);
        if (
          Array.isArray(parsedDateRange) &&
          parsedDateRange.length === 2 &&
          parsedDateRange[0] &&
          parsedDateRange[1]
        ) {
          // Converte strings ISO para objetos dayjs
          const startDate = dayjs(parsedDateRange[0]);
          const endDate = dayjs(parsedDateRange[1]);

          // Verifica se as datas são válidas
          if (startDate.isValid() && endDate.isValid()) {
            const nextRange = [startDate, endDate];
            setDateRange(nextRange);
            setDraftDateRange(nextRange);
            setSearchedColumn("dateColeta");
          } else {
            setDateRange([]);
            setDraftDateRange([]);
          }
        } else {
          setDateRange([]);
          setDraftDateRange([]);
        }
      } else {
        setDateRange([]);
        setDraftDateRange([]);
      }
    } catch (error) {
      console.error("Erro ao carregar filtros do localStorage:", error);
      setColumnFilters({});
      setDateRange([]);
      setDraftDateRange([]);
      setSorterConfig({ columnKey: null, order: null });
      setColumnOrder(null);
    }

    hasLoadedRef.current = true;
  }, [user?.uid]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!user?.uid) return;

    // Verifica se dateRange é um array com elementos válidos
    if (
      dateRange &&
      Array.isArray(dateRange) &&
      dateRange.length > 0 &&
      dateRange[0] &&
      dateRange[1]
    ) {
      localStorage.setItem(
        storageKey(user.uid, "freightDateRangeFilter"),
        JSON.stringify(dateRange),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightDateRangeFilter"));
    }

    // Verifica se columnFilters tem pelo menos uma chave
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      localStorage.setItem(
        storageKey(user.uid, "freightColumnFilters"),
        JSON.stringify(columnFilters),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightColumnFilters"));
    }

    if (sorterConfig?.columnKey && sorterConfig?.order) {
      localStorage.setItem(
        storageKey(user.uid, "freightTableSorter"),
        JSON.stringify(sorterConfig),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightTableSorter"));
    }

    if (Array.isArray(columnOrder) && columnOrder.length > 0) {
      localStorage.setItem(
        storageKey(user.uid, "freightColumnOrder"),
        JSON.stringify(columnOrder),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightColumnOrder"));
    }
  }, [user?.uid, dateRange, columnFilters, sorterConfig, columnOrder]);

  // ========================
  // Limpar filtros via prop externa
  // ========================
  useEffect(() => {
    if (cleanFilter) {
      recentlyDeletedIdsRef.current.clear();
      setColumnFilters({});
      setDateRange([]);
      setDraftDateRange([]);
      setSearchText("");
      setSearchedColumn("");
      setSorterConfig({ columnKey: null, order: null });
      setColumnOrder(null);
      if (user?.uid) {
        localStorage.removeItem(storageKey(user.uid, "freightDateRangeFilter"));
        localStorage.removeItem(storageKey(user.uid, "freightColumnFilters"));
        localStorage.removeItem(storageKey(user.uid, "freightTableSorter"));
        localStorage.removeItem(storageKey(user.uid, "freightColumnOrder"));
      }
      if (onCleanFilterHandled) {
        onCleanFilterHandled();
      }
    }
  }, [cleanFilter, user?.uid, onCleanFilterHandled]);

  // ========================
  // Abrir modal de filtros salvos via prop externa
  // ========================
  useEffect(() => {
    if (openFilter) {
      setOpenModalFilter(true);
      if (onOpenFilterHandled) {
        onOpenFilterHandled();
      }
    }
  }, [openFilter, onOpenFilterHandled]);

  // ========================
  // Modal fila (API-only)
  // ========================
  const handleOpen = () => setOpenModal(true);

  const handleClose = () => {
    setOpenModal(false);
    setDriversInFila([]);
  };

  const handleOpenTransport = () => setOpenTransportModal(true);

  const handleCloseTransport = () => {
    setOpenTransportModal(false);
    setTransportsInFila([]);
    setTransportFreightId(null);
    setTransportFreightShipperUid(null);
    setAcceptingTransportId(null);
  };

  const handleApplyCustomFilter = (filter) => {
    // Limpar filtros existentes antes de aplicar o novo
    const newFilters = {};
    let newDateRange = null;

    // Processar cada condição do filtro
    if (filter.conditions && filter.conditions.length > 0) {
      filter.conditions.forEach((condition) => {
        const { column, operator, value } = condition;

        // Tratar filtros de data separadamente
        if (column === "firstDelivery.dateColeta") {
          if (
            operator === "between" &&
            value &&
            Array.isArray(value) &&
            value.length === 2
          ) {
            const startDate =
              typeof value[0] === "string" ? dayjs(value[0]) : value[0];
            const endDate =
              typeof value[1] === "string" ? dayjs(value[1]) : value[1];
            newDateRange = [startDate, endDate];
          } else if (operator === "equals" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, date];
          } else if (operator === "after" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, null];
          } else if (operator === "before" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [null, date];
          } else if (value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, date];
          }
        }

        // Tratar filtros de status (valor único — GET /freight/v2)
        else if (column === "status.describe") {
          if (operator === "in" && Array.isArray(value) && value.length > 0) {
            newFilters[column] = value;
          } else if (operator === "equals" && value != null && value !== "") {
            const normalizedValue = Array.isArray(value) ? value[0] : value;
            newFilters[column] = [normalizedValue];
          }
        }
        // Tratar filtros numéricos com operadores condicionais
        else if (
          (column === "freight.valueFreightage" ||
            column === "driver.valueNegotiated") &&
          value
        ) {
          newFilters[column] = {
            operator,
            value: parseFloat(value) || 0,
          };
        }
        // Tratar outros filtros de texto/número, Para operador 'contains' ou 'equals' em campos de texto
        else {
          if (
            operator === "contains" ||
            operator === "equals" ||
            operator === "startsWith" ||
            operator === "endsWith"
          ) {
            newFilters[column] = value;
          }
        }
      });
    }

    // Aplicar os filtros de forma síncrona, primeiro aplicar o dateRange, depois os columnFilters
    if (newDateRange) {
      setDateRange(newDateRange);
      setDraftDateRange(newDateRange);
    }

    // Aplicar os filtros de coluna, bug encontrado de column + dateRange por isso essa ordem
    setColumnFilters(newFilters);
  };

  const getDriversQueue = async (id) => {
    try {
      const res = await freightApi2.queue(id);
      const queue = Array.isArray(res.data) ? res.data : res.data?.items || [];
      cacheSet(queueCountCacheRef.current, id, queue.length, TTL_QUEUE_COUNT_MS);
      setDriversInFila(queue);
      handleOpen();
    } catch (e) {
      message.error("Erro ao buscar fila do frete");
    }
  };

  const getTransportQueue = async (id, shipperUid) => {
    try {
      const isShipper = shipperUid || user?.uidShipper || "";
      if (!isShipper) {
        message.error("Embarcador não identificado para consultar a fila.");
        return;
      }

      const res = await freightApi2.transportQueue(id, isShipper);
      const queue = parseTransportQueueResponse(res);
      cacheSet(
        transportQueueCountCacheRef.current,
        `${id}:${isShipper}`,
        queue.length,
        TTL_QUEUE_COUNT_MS,
      );
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [id]: { count: queue.length, loading: false },
      }));
      setTransportsInFila(queue);
      setTransportFreightId(id);
      setTransportFreightShipperUid(isShipper);
      handleOpenTransport();
    } catch (e) {
      message.error("Erro ao buscar fila de transportadoras");
    }
  };

  const handleAcceptTransport = async (transport) => {
    if (!transportFreightId || !transport?.idTransport || acceptingTransportId) {
      return;
    }

    setAcceptingTransportId(transport.idTransport);

    try {
      await acceptTransportFromQueue({
        freightId: transportFreightId,
        transport,
        user,
      });

      message.success("Transportadora aceita com sucesso.");

      const isShipper =
        transportFreightShipperUid || user?.uidShipper || "";
      const res = await freightApi2.transportQueue(
        transportFreightId,
        isShipper,
      );
      const queue = parseTransportQueueResponse(res);
      setTransportsInFila(queue);
      cacheSet(
        transportQueueCountCacheRef.current,
        `${transportFreightId}:${isShipper}`,
        queue.length,
        TTL_QUEUE_COUNT_MS,
      );
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [transportFreightId]: { count: queue.length, loading: false },
      }));

      if (!queue.length) {
        handleCloseTransport();
        setTransportFreightId(null);
        setTransportFreightShipperUid(null);
      }
    } catch (error) {
      console.error("Erro ao aceitar transportadora:", error);
      message.error(
        getAcceptTransportErrorMessage(
          error,
          "Não foi possível aceitar a transportadora.",
        ),
      );
    } finally {
      setAcceptingTransportId(null);
    }
  };

  // ========================
  // Delete (API-only)
  // ========================

  const [open, setOpen] = React.useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const handleCloseYesOrNot = () => {
    setOpen(false);
    setPendingDeleteId(null);
  };
  const handleOpenYesOrNot = () => setOpen(true);

  const handleModalResult = async (value) => {
    if (!value || !pendingDeleteId) return;

    try {
      stoppingPointsCacheRef.current.delete(pendingDeleteId);
      queueCountCacheRef.current.delete(pendingDeleteId);
      deleteTransportQueueCacheForFreight(
        transportQueueCountCacheRef.current,
        pendingDeleteId,
      );
      setQueueUiByFreightId((prev) => {
        const next = { ...prev };
        delete next[pendingDeleteId];
        return next;
      });
      setTransportQueueUiByFreightId((prev) => {
        const next = { ...prev };
        delete next[pendingDeleteId];
        return next;
      });
      await freightApi2.delete(pendingDeleteId);
      message.success("Frete exclu??do com sucesso!");
      recentlyDeletedIdsRef.current.add(pendingDeleteId);
      fetchData({ skipStaleHydrate: true, page: current });
    } catch (error) {
      message.error("Erro ao excluir o frete");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const deleteEmb = (id) => {
    setPendingDeleteId(id);
    handleOpenYesOrNot();
  };

  // ========================
  // Filtros AntD (igual ao seu)
  // ========================
  const handleSearch = (selectedKeys, confirm, columnKey) => {
    setSearchText(selectedKeys[0]?.toString() ?? "");
    confirm();
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]:
        columnKey === "freight.valueFreightage" ||
          columnKey === "driver.valueNegotiated"
          ? moneyToInt(selectedKeys)
          : (selectedKeys.toString() ?? ""),
    }));
  };

  const handleSearchDate = (confirm) => {
    setDateRange(draftDateRange && draftDateRange.length ? draftDateRange : []);
    confirm();
    setSearchText("");
    setSearchedColumn("dateColeta");
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setColumnFilters((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[searchedColumn];
      return updatedFilters;
    });
  };

  const handleResetDate = (clearFilters) => {
    clearFilters();
    setDateRange([]);
    setDraftDateRange([]);
  };

  const getColumnSearchProps = (dataIndex, type = "text", maxLength = 40) => {
    const columnKey = Array.isArray(dataIndex)
      ? dataIndex.join(".")
      : dataIndex;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys = [],
        confirm,
        clearFilters,
        close,
      }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Buscar ${columnKey}`}
            value={selectedKeys[0] ?? ""}
            maxLength={type === "text" ? maxLength : undefined}
            onChange={(e) => {
              let value = e.target.value ?? "";
              if (type === "number") {
                value = value.replace(/\D/g, "");
              } else if (type === "money") {
                value = value.replace(/\D/g, "");
                value = (Number(value) / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              } else if (type === "text") {
                try {
                  value = value.replace(/[0-9]/g, "");
                } catch {
                  value = value.replace(/\d/g, "");
                }
              }
              setSelectedKeys(value ? [value] : []);
            }}
            onPressEnter={() => handleSearch(selectedKeys, confirm, columnKey)}
            style={{ marginBottom: 8, display: "block" }}
          />

          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, columnKey)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Buscar
            </Button>

            <Button
              onClick={() => {
                if (clearFilters) handleReset(clearFilters);

                columnFilters[columnKey] &&
                  setColumnFilters((prev) => {
                    const updatedFilters = { ...prev };
                    delete updatedFilters[columnKey];
                    return updatedFilters;
                  });

                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reiniciar
            </Button>

            <Button type="link" size="small" onClick={() => close()}>
              Fechar
            </Button>
          </Space>
        </div>
      ),

      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),

      // Filtros aplicados no servidor (GET /freight/v2)
      onFilter: () => true,

      onFilterDropdownOpenChange: (visible) => {
        if (visible) setTimeout(() => searchInput.current?.select(), 100);
      },

      render: (text) =>
        searchedColumn === columnKey ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={searchText ? [searchText] : []}
            autoEscape
            sanitize={foldDiacritics}
            textToHighlight={text ? text.toString() : ""}
          />
        ) : (
          text
        ),
    };
  };

  const getColumnSearchPropsDate = () => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePickerMenu
          value={draftDateRange}
          onChange={(dates) => {
            handleDateRangeChange(dates);
            setSelectedKeys(dates ? [dates] : []);
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearchDate(confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleResetDate(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reiniciar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: () => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === "dateColeta" ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  /** Filtro de status multi-checkbox com envio futuro de vários valores na API */
  const getStatusFilterProps = () => {
    const selectedValues = Array.isArray(columnFilters["status.describe"])
      ? columnFilters["status.describe"]
      : [];

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys = [],
        confirm,
        clearFilters,
      }) => {
        const currentSelected =
          Array.isArray(selectedKeys) && selectedKeys.length
            ? selectedKeys
            : selectedValues;
        const filteredOptions = filterStatusFilterOptions(statusSearchText);
        const allSelected =
          filteredOptions.length > 0 &&
          filteredOptions.every((option) => currentSelected.includes(option));
        const someSelected =
          filteredOptions.some((option) => currentSelected.includes(option)) &&
          !allSelected;

        const updateSelection = (nextValues) => {
          const normalized = Array.from(new Set(nextValues)).filter(Boolean);
          setSelectedKeys(normalized);
        };

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={statusFilterRef}
              placeholder="Buscar..."
              value={statusSearchText}
              onChange={(e) => setStatusSearchText(e.target.value)}
              style={{ marginBottom: 8 }}
              allowClear
              onPressEnter={() => confirm()}
            />

            <div style={{ maxHeight: 250, overflowY: "auto", marginBottom: 8 }}>
              <div style={{ marginBottom: 4 }}>
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) =>
                    updateSelection(
                      e.target.checked
                        ? Array.from(
                            new Set([...currentSelected, ...filteredOptions]),
                          )
                        : currentSelected.filter(
                            (value) => !filteredOptions.includes(value),
                          ),
                    )
                  }
                >
                  (Selecionar todos)
                </Checkbox>
              </div>

              {filteredOptions.map((value) => (
                <div key={value} style={{ marginBottom: 4 }}>
                  <Checkbox
                    checked={currentSelected.includes(value)}
                    onChange={(e) =>
                      updateSelection(
                        e.target.checked
                          ? [...currentSelected, value]
                          : currentSelected.filter((v) => v !== value),
                      )
                    }
                  >
                    {statusFilterOptionLabel(value)}
                  </Checkbox>
                </div>
              ))}
            </div>

            <Space>
              <Button
                onClick={() => {
                  if (clearFilters) clearFilters();
                  setSelectedKeys([]);
                  setStatusSearchText("");
                  setColumnFilters((prev) => {
                    const updated = { ...prev };
                    delete updated["status.describe"];
                    return updated;
                  });
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Resetar
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  const nextValues = Array.isArray(selectedKeys)
                    ? selectedKeys
                    : [];
                  setColumnFilters((prev) => {
                    const updated = { ...prev };
                    if (nextValues.length) updated["status.describe"] = nextValues;
                    else delete updated["status.describe"];
                    return updated;
                  });
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Aplicar
              </Button>
            </Space>
          </div>
        );
      },
      filterMultiple: true,
      onFilter: () => true,
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => statusFilterRef.current?.select(), 100);
          setStatusSearchText("");
        }
      },
    };
  };

  // Ordenação por prioridade (igual à sua ideia)
  const statusPriority = useMemo(
    () => ({
      Entregue: 1,
      "Em trânsito": 2,
      [FREIGHT_IN_TRANSIT_DESCRIBE]: 2,
      Contratado: 3,
      "Pendente de contratação": 4,
      "Pendente de validação": 5,
      Finalizado: 6,
    }),
    [],
  );

  const savedFilterStatusOptions = useMemo(
    () =>
      STATUS_FILTER_OPTION_VALUES.map((value) => ({
        value,
        label: statusFilterOptionLabel(value),
      })),
    [],
  );

  const baseColumns = [
    {
      title: "Data Coleta",
      dataIndex: ["firstDelivery", "dateColeta"],
      key: "dateColeta",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "dateColeta" ? sorterConfig.order : null,
      filteredValue: dateRange && dateRange.length ? [dateRange] : null,
      ...getColumnSearchPropsDate(),
      sorter: (a, b) => {
        const dateA = a.firstDelivery?.dateColeta;
        const dateB = b.firstDelivery?.dateColeta;

        if (!dateA) return -1;
        if (!dateB) return 1;

        const [yearA, monthA, dayA] = dateA.split("-").map(Number);
        const [yearB, monthB, dayB] = dateB.split("-").map(Number);

        return (
          new Date(yearA, monthA - 1, dayA).getTime() -
          new Date(yearB, monthB - 1, dayB).getTime()
        );
      },
      render: (dataColeta) => {
        if (dataColeta && dataColeta !== "") {
          const [year, month, day] = dataColeta.split("-");
          const localDate = new Date(+year, +month - 1, +day);
          return format(localDate, "dd/MM/yyyy");
        }
        return "";
      },
    },
    {
      title: "Código Frete",
      dataIndex: "numberSerial",
      key: "numberSerial",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "numberSerial" ? sorterConfig.order : null,
      filteredValue: columnFilters["numberSerial"]
        ? [columnFilters["numberSerial"]]
        : null,
      ...getColumnSearchProps("numberSerial", "number"),
      sorter: (a, b) =>
        (a.numberSerial?.toString() || "").localeCompare(
          b.numberSerial?.toString() || "",
        ),
      render: (value) => <span>{value}</span>,
    },
    {
      title: "Nome Pagador",
      dataIndex: ["clientPayment", "name"],
      key: "nameClient",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "nameClient" ? sorterConfig.order : null,
      filteredValue: columnFilters["clientPayment.name"]
        ? [columnFilters["clientPayment.name"]]
        : null,
      ...getColumnSearchProps(["clientPayment", "name"], "text", 40),
      sorter: (a, b) =>
        (a.clientPayment?.name || "").localeCompare(
          b.clientPayment?.name || "",
        ),
    },
    {
      title: 'Nome Motorista',
      dataIndex: ['freight', 'getDriverFreight', 'name'],
      key: 'nameDriver',
      align: 'center',
      filteredValue: columnFilters['freight.getDriverFreight.name'] ? [columnFilters['freight.getDriverFreight.name']] : null,
      ...getColumnSearchProps(['freight', 'getDriverFreight', 'name'], 'text', 40),
      sorter: (a, b) => {
        const nameA = a.driver?.name || '';
        const nameB = b.driver?.name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Origem",
      dataIndex: ["firstDelivery", "city"],
      key: "firstDeliveryCity",
      // align: "center",
      sortOrder:
        sorterConfig.columnKey === "firstDeliveryCity"
          ? sorterConfig.order
          : null,
      filteredValue: columnFilters["firstDelivery.city"]
        ? [columnFilters["firstDelivery.city"]]
        : null,
      ...getColumnSearchProps(["firstDelivery", "city"], "text", 40),
      render: (_, record) => (
        <span>
          {record?.firstDelivery?.city || ""}{" "}
          {record?.firstDelivery?.uf
            ? `- ${record.firstDelivery.uf}`
            : ""}
        </span>
      ),
      sorter: (a, b) =>
        (a.firstDelivery?.city || "").localeCompare(
          b.firstDelivery?.city || "",
        ),
    },
    {
      title: "Destino",
      dataIndex: ["lastDelivery", "city"],
      key: "lastDeliveryCity",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "lastDeliveryCity"
          ? sorterConfig.order
          : null,
      filteredValue: columnFilters["lastDelivery.city"]
        ? [columnFilters["lastDelivery.city"]]
        : null,
      ...getColumnSearchProps(["lastDelivery", "city"], "text", 40),
      render: (_, record) => (
        <span>{record?.lastDelivery?.city || ""} - {record?.lastDelivery?.uf || ""}</span>
      ),
      sorter: (a, b) =>
        (a.lastDelivery?.city || "").localeCompare(b.lastDelivery?.city || ""),

    },
    ...(canViewFreightFreight ? [
      {
        title: 'Valor do Frete',
        dataIndex: ['freight', 'valueFreightage'],
        key: 'freight',
        align: 'center',
        // width: '22%',
        filteredValue: columnFilters['freight.valueFreightage'] ? [columnFilters['freight.valueFreightage']] : null,
        ...getColumnSearchProps(['freight', 'valueFreightage'], 'money'),
        render: (valueFreightage) => (
          `${(valueFreightage || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`
        ),
        sorter: (a, b) => {
          const valueA = a.freight?.valueFreightage || 0;
          const valueB = b.freight?.valueFreightage || 0;
          return valueA - valueB;
        },
      },
    ] : []),
    ...(canViewFreightDriver ? [
      {
        title: 'Pagando ao Motorista',
        dataIndex: ['driver', 'valueNegotiated'],
        key: 'valueNegotiated',
        align: 'center',
        // width: '30%',
        filteredValue: columnFilters['driver.valueNegotiated'] ? [columnFilters['driver.valueNegotiated']] : null,
        ...getColumnSearchProps(['driver', 'valueNegotiated'], 'money'),
        render: (driver) => (
          `${(driver || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`
        ),
        sorter: (a, b) => {
          const valueA = a.driver?.valueInitial || 0;
          const valueB = b.driver?.valueInitial || 0;
          return valueA - valueB;
        },
      },
    ] : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "status" ? sorterConfig.order : null,
      filteredValue:
        Array.isArray(columnFilters["status.describe"]) &&
        columnFilters["status.describe"].length
          ? columnFilters["status.describe"]
          : null,
      ...getStatusFilterProps(),
      render: (status, record) => {
        const effectiveStatusDescribe = getStatusDescribeForRecord(record);
        const describeText =
          effectiveStatusDescribe === "Pendente de validação"
            ? "P. Validação"
            : effectiveStatusDescribe === "Pendente de contratação"
              ? "P.Contratação"
              : effectiveStatusDescribe;
        return (
          <Tooltip title={effectiveStatusDescribe}>
            <div>{renderStatusColor(describeText)}</div>
          </Tooltip>
        );
      },
      sorter: (a, b) =>
        getStatusDescribeForRecord(a).localeCompare(getStatusDescribeForRecord(b)),
    },
    {
      title: "Ações",
      align: "center",
      key: "action",
      render: (_, record) => {
        const handleMenuClick = ({ key }) => {
          if (key === "queue") getDriversQueue(record.id);
          if (key === "transportQueue") {
            getTransportQueue(record.id, resolveEmbarcadorUid(record, user));
          }
          if (key === "delete") deleteEmb(record.id);
        };

        const menu = (
          <Menu
            onClick={handleMenuClick}
            style={{
              minWidth: 200,
              padding: "8px 0",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            <Menu.Item key="queue" style={menuItemStyle}>
              <Link
                to={`#${record.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
              >
                <DriverBadge
                  recordId={record.id}
                  statusDescribe={getStatusDescribeForRecord(record)}
                  queueCount={queueUiByFreightId[record.id]?.count ?? 0}
                  queueLoading={!!queueUiByFreightId[record.id]?.loading}
                />
                Ver Fila
              </Link>
            </Menu.Item>

            <Menu.Item key="transportQueue" style={menuItemStyle}>
              <Link
                to={`#${record.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
              >
                <TransportBadge
                  recordId={record.id}
                  queueCount={
                    transportQueueUiByFreightId[record.id]?.count ?? 0
                  }
                  queueLoading={
                    !!transportQueueUiByFreightId[record.id]?.loading
                  }
                />
                Transportadora
              </Link>
            </Menu.Item>

            <Menu.Item key="edit" style={menuItemStyle}>
              {user?.permissoes?.freight?.isEdit ||
                user?.permissoes?.frete?.editar ? (
                <Link
                  to={`/freightCopy/${record.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <EditOutlined style={{ fontSize: 18 }} />
                  Editar frete
                </Link>
              ) : (
                <Link
                  to={`/freightCopy/${record.id}`}
                  state={{ viewOnly: true }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                  Visualizar frete
                </Link>
              )}
            </Menu.Item>

            <Menu.Item key="copy" style={menuItemStyle}>
              <Link
                to={`/freightCopy/${record.id}?copy=true`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
              >
                <CopyOutlined style={{ fontSize: 18 }} />
                Copiar
              </Link>
            </Menu.Item>

            <div
              style={{
                height: 1,
                background: "rgba(1,36,66,0.1)",
                margin: "6px 8px",
              }}
            />

            <Menu.Item
              key="delete"
              style={{
                ...menuItemStyle,
                color: "#ff0000",
              }}
            >
              {user?.permissoes?.freight?.isDelete ||
                user?.permissoes?.frete?.excluir ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <DeleteOutlined style={{ fontSize: 18 }} />
                  Excluir frete
                </div>
              ) : null}
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown
            overlay={menu}
            trigger={["click"]}
            placement="bottomRight"
            onOpenChange={(open) => {
              if (open) {
                prefetchQueueCountForFreight(record.id);
                prefetchTransportQueueCountForFreight(
                  record.id,
                  resolveEmbarcadorUid(record, user),
                );
              }
            }}
          >
            <Button
              type="text"
              icon={<SmallDashOutlined style={{ fontSize: 20 }} />}
            />
          </Dropdown>
        );
      },
    },
  ];

  const handleHeaderDragState = useCallback((evt) => {
    if (!evt) return;
    const { type, columnKey } = evt;
    if (type === "start") {
      draggingKeyRef.current = columnKey;
      setDragOverKey(null);
    } else if (type === "over") {
      const dragging = draggingKeyRef.current;
      if (dragging && dragging !== columnKey) {
        setDragOverKey((prev) => (prev === columnKey ? prev : columnKey));
      }
    } else if (type === "leave") {
      setDragOverKey((prev) => (prev === columnKey ? null : prev));
    } else if (type === "end") {
      draggingKeyRef.current = null;
      setDragOverKey(null);
    }
  }, []);

  const handleReorderColumn = useCallback((fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const currentKeys = orderedColumnsKeysRef.current;
    if (!Array.isArray(currentKeys) || !currentKeys.length) return;
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = reorderArray(currentKeys, fromIdx, toIdx);
    setColumnOrder(next);
  }, []);

  const orderedColumns = useMemo(() => {
    const ordered = applyColumnOrder(baseColumns, columnOrder);
    return ordered.map((col) => ({
      ...col,
      onHeaderCell: () => ({
        columnKey: col.key,
        isDragging: draggingKeyRef.current === col.key,
        isDragOver: dragOverKey === col.key,
        onReorder: handleReorderColumn,
        onDragStateChange: handleHeaderDragState,
      }),
    }));

  }, [baseColumns, columnOrder, dragOverKey, handleReorderColumn, handleHeaderDragState]);

  useEffect(() => {
    orderedColumnsKeysRef.current = orderedColumns
      .map((c) => c.key)
      .filter(Boolean);
  }, [orderedColumns]);

  // Preserve backend ordering from /freight/v2 (no implicit local rank sort).
  const sortedPageData = useMemo(() => datas, [datas]);

  const paginationEstimatedTotal =
    (current - 1) * pageSize +
    datas.length +
    (listHasMore ? pageSize : 0);

  return (
    <>
      <DialogFileDriver
        driversInFila={driversInFila}
        openModal={openModal}
        handleClose={handleClose}
      />

      <DialogFilaTransport
        transportsInFila={transportsInFila}
        openModal={openTransportModal}
        handleClose={handleCloseTransport}
        acceptingTransportId={acceptingTransportId}
        onAcceptTransport={handleAcceptTransport}
        canAccept={Boolean(user?.uidShipper)}
      />

      <ModalFilter
        visible={openModalFilter}
        onClose={() => setOpenModalFilter(false)}
        onApply={handleApplyCustomFilter}
        selectOptionsMap={{ "status.describe": savedFilterStatusOptions }}
      />

      <YesOrNot
        open={open}
        handleClose={handleCloseYesOrNot}
        onResult={handleModalResult}
      />

      {/* {isFilterApplying ? (
        <Alert
          type="info"
          showIcon
          message="Aplicando filtros, aguarde..."
          style={{ marginBottom: 8 }}
        />
      ) : null}
      {refreshing && !isFilterApplying ? (
        <Alert
          type="info"
          showIcon
          message="Atualizando dados em segundo plano..."
          style={{ marginBottom: 8 }}
        />
      ) : null} */}

      {/* <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 10,
          marginBottom: 8,
        }}
      >
        {refreshing ? <Spin size="small" /> : null}
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={() =>
            fetchData({ forceRefresh: true, page: current })
          }
        >
          Atualizar dados
        </Button>
      </div> */}

      <Table
        columns={orderedColumns}
        components={{ header: { cell: DraggableHeaderCell } }}
        dataSource={sortedPageData}
        rowKey="id"
        loading={loading || isFilterApplying || filterFetchingAll}
        onChange={(_pagination, _filters, sorter) => {
          if (sorter && sorter.columnKey && sorter.order) {
            setSorterConfig({
              columnKey: sorter.columnKey,
              order: sorter.order,
            });
          } else {
            setSorterConfig({ columnKey: null, order: null });
          }
        }}
        pagination={
          serverFiltersActive
            ? {
                current: 1,
                pageSize: Math.max(datas.length, 1),
                total: datas.length,
                showSizeChanger: false,
                hideOnSinglePage: false,
              }
            : {
                current,
                pageSize,
                total: paginationEstimatedTotal,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
                showQuickJumper: false,
                hideOnSinglePage: false,
                onChange: (page, newPageSize) => {
                  if (newPageSize && newPageSize !== pageSize) {
                    setPageSize(newPageSize);
                    setCurrent(1);
                    return;
                  }
                  if (page > current && !listHasMore) {
                    message.warning("Nao ha mais dados para proxima pagina.");
                    return;
                  }
                  setCurrent(page);
                },
              }
        }
      />
    </>
  );
};

export default DataFreteTableCopy;
